import { Box, Container, Stack, Toolbar } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import { Fragment, type Dispatch, type SetStateAction } from "react";
import { ChatbotDrawer } from "./ChatbotDrawer.component";
import { CloseDropdowns } from "./CloseDropdowns.component";
import { HeaderActionsBar } from "./HeaderActionsBar.component";
import { LanguageSelector } from "./LanguageSelector.component";
import { LinkLogo } from "./LinkLogo.component";
import { NotificationsPopOver } from "./NotificationsPopOver.component";
import { SearchBox } from "./SearchBox.component";
import { SupportAiButton } from "./SupportAiButton.component";
import { LanguageModal } from "../../../components";
import { ImpersonationModal } from "../../../components/ImpersonationModal";
import type { Language } from "../../../types";
import { DROPDOWN, type Dropdown } from "../Header.consts";
import { HeaderAppBar } from "../Header.styles";

type HeaderDesktopProps = {
  authenticated: boolean
  isMultilanguage?: boolean
  languages?: Language[]
  isPreLogin?: boolean
  dropdown: Dropdown | null
  setDropdown: Dispatch<SetStateAction<Dropdown | null>>
  anchorElDropdown: HTMLElement | null
  setAnchorElDropdown: Dispatch<SetStateAction<HTMLElement | null>>
  anchorElNotify: HTMLElement | null
  setAnchorElNotify: Dispatch<SetStateAction<HTMLElement | null>>
  mobileSearchOpen: boolean
  searchValue: string
  setSearchValue: Dispatch<SetStateAction<string>>
  languageModalOpen: boolean
  setLanguageModalOpen: Dispatch<SetStateAction<boolean>>
  impersonationModalOpen: boolean
  setImpersonationModalOpen: Dispatch<SetStateAction<boolean>>
}

export function HeaderDesktop({
  authenticated,
  isMultilanguage,
  languages,
  isPreLogin,
  dropdown,
  setDropdown,
  anchorElDropdown,
  setAnchorElDropdown,
  anchorElNotify,
  setAnchorElNotify,
  mobileSearchOpen,
  searchValue,
  setSearchValue,
  languageModalOpen,
  setLanguageModalOpen,
  impersonationModalOpen,
  setImpersonationModalOpen
}: HeaderDesktopProps) {
  const theme = useTheme();

  return (
    <HeaderAppBar $mobileSearchOpen={ mobileSearchOpen } id="app-bar" role="banner" tabIndex={ -1 }>
      { dropdown && <CloseDropdowns closeDropdowns={ () => { setDropdown(null) } }/> }
      <ChatbotDrawer
        hideChatbot={ () => setDropdown(null) }
        showChatBot={ dropdown === DROPDOWN.CHATBOT }
      />
      {
        authenticated ?
          <NotificationsPopOver
            anchorElNotify={ anchorElNotify }
            hideNotifications={ () => setDropdown(null) }
            setAnchorElNotify={ setAnchorElNotify }
            showNotifications={ dropdown === DROPDOWN.NOTIFICATIONS }
          /> :
          null
      }

      <Container
        maxWidth="max"
        sx={ { "& .MuiToolbar-root": { paddingLeft: "18px" } } }
      >
        <Toolbar sx={ { p: "0 18px" } }>
          { /* left side elements */ }
          <Box sx={ {
            width: "182px"
          } }>
            <LinkLogo authenticated={ authenticated }/>
          </Box>
          { /* center elements */ }
          {
            authenticated ? (
              <SearchBox
                setDropdown={ setDropdown }
                searchValue={ searchValue }
                setSearchValue={ setSearchValue }
                showSearchResults={ dropdown === DROPDOWN.SEARCH }
              />
            ) : null
          }
          { /* right side elements */ }
          {
            authenticated ? (
              <Fragment>
                <HeaderActionsBar
                  variant="desktop"
                  isMultilanguage={ Boolean(isMultilanguage) }
                  dropdown={ dropdown }
                  setDropdown={ setDropdown }
                  anchorElDropdown={ anchorElDropdown }
                  setAnchorElDropdown={ setAnchorElDropdown }
                  setAnchorElNotify={ setAnchorElNotify }
                  setLanguageModalOpen={ setLanguageModalOpen }
                  setImpersonationModalOpen={ setImpersonationModalOpen }
                />
              </Fragment>
            ) : null
          }
          {
            !authenticated && (isPreLogin || isMultilanguage) ? (
              <Stack
                alignItems="center"
                direction="row"
                flexGrow={ 1 }
                gap={ 2 }
                justifyContent="flex-end"
                zIndex={ theme.zIndex.fab }
              >
                { isPreLogin ? <SupportAiButton /> : null }
                { isMultilanguage ? (
                  <LanguageSelector setOpenLanguageModal={ setLanguageModalOpen } />
                ) : null }
              </Stack>
            ) : null
          }
        </Toolbar>
      </Container>
      { languageModalOpen && (
        <LanguageModal
          close={ () => {setLanguageModalOpen(false)} }
          languages={ languages ?? [] }
          open={ languageModalOpen }
        />
      )
      }
      <ImpersonationModal
        close={ () => setImpersonationModalOpen(false) }
        open={ impersonationModalOpen }
      />
    </HeaderAppBar>
  );
}
